import React, { useState } from 'react';
import { 
    Form, 
    Input, 
    Select, 
    DatePicker, 
    Button, 
    Card, 
    message,
    Space,
    Steps,
    Spin,
    Alert,
    Modal,
    Descriptions,
    Badge
} from 'antd';
import { ArrowLeftOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import settlementApi from '../../../api/settlementApi';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

/**
 * 批次核算创建页
 */
const BatchCalculationCreatePage = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [calculating, setCalculating] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    // 提交表单
    const handleSubmit = async (values, replaceExisting = false) => {
        try {
            setLoading(true);
            setCalculating(true);

            // 格式化日期
            const data = {
                batch_name: values.batch_name,
                type: values.type,
                start_date: values.date_range[0].format('YYYY-MM-DD'),
                end_date: values.date_range[1].format('YYYY-MM-DD'),
                description: values.description || undefined,
                replace_existing: replaceExisting
            };

            message.loading({ content: '正在创建批次...', key: 'batch', duration: 0 });

            const response = await settlementApi.batchCalculations.createBatchCalculation(data);
            
            message.success({ 
                content: '批次已创建，正在后台处理...', 
                key: 'batch',
                duration: 3
            });
            
            // 开始轮询批次状态
            const batchId = response.data.batch_id;
            startBatchPolling(batchId, values.batch_name);

        } catch (error) {
            console.error('创建批次失败:', error);
            
            // 处理批次冲突
            if (error.response?.status === 409) {
                handleBatchConflict(error.response.data.detail, values);
                return;
            }
            
            const errorMsg = error.response?.data?.detail?.message || error.message || '创建失败';
            message.error({ content: `创建批次失败: ${errorMsg}`, key: 'batch' });
            setCalculating(false);
        } finally {
            setLoading(false);
        }
    };

    // 开始轮询批次状态
    const startBatchPolling = (batchId, batchName) => {
        let pollCount = 0;
        const maxPolls = 120; // 最多轮询10分钟（每5秒一次）
        
        message.loading({ 
            content: `批次 "${batchName}" 正在处理中...`, 
            key: `polling_${batchId}`, 
            duration: 0 
        });

        const pollInterval = setInterval(async () => {
            try {
                pollCount++;
                const statusResponse = await settlementApi.batchCalculations.getBatchCalculationStatus(batchId);
                const status = statusResponse.data.status;
                
                console.log(`批次 ${batchId} 状态: ${status} (第${pollCount}次查询)`);

                if (status === 'COMPLETED') {
                    // 处理完成
                    clearInterval(pollInterval);
                    message.success({ 
                        content: `批次 "${batchName}" 处理完成！`, 
                        key: `polling_${batchId}`,
                        duration: 4
                    });
                    
                    // 跳转到详情页
                    setTimeout(() => {
                        navigate(`/settlement/batch-calculations/${batchId}`);
                    }, 1000);
                    
                } else if (status === 'FAILED') {
                    // 处理失败
                    clearInterval(pollInterval);
                    const errorMsg = statusResponse.data.error_message || '未知错误';
                    message.error({ 
                        content: `批次 "${batchName}" 处理失败: ${errorMsg}`, 
                        key: `polling_${batchId}`,
                        duration: 8
                    });
                    
                    setCalculating(false);
                    
                } else if (status === 'NEEDS_RECALCULATION') {
                    // 需要重新计算（数据源已变更）
                    clearInterval(pollInterval);
                    message.warning({ 
                        content: `批次 "${batchName}" 因数据源变更，需要重新计算`, 
                        key: `polling_${batchId}`,
                        duration: 6
                    });
                    
                    setCalculating(false);
                    
                } else if (pollCount >= maxPolls) {
                    // 超时停止轮询
                    clearInterval(pollInterval);
                    message.warning({ 
                        content: `批次 "${batchName}" 处理时间较长，请手动前往列表页查看结果`, 
                        key: `polling_${batchId}`,
                        duration: 6
                    });
                    
                    setCalculating(false);
                    
                } else if (status === 'RUNNING') {
                    // 继续轮询，更新进度信息
                    const progress = statusResponse.data.progress;
                    if (progress) {
                        message.loading({ 
                            content: `批次 "${batchName}" 处理中... (已用时: ${progress.elapsed_formatted})`, 
                            key: `polling_${batchId}`, 
                            duration: 0 
                        });
                    }
                }

            } catch (error) {
                console.error(`轮询批次 ${batchId} 状态失败:`, error);
                
                // 如果是网络错误，继续轮询几次
                if (pollCount < 5) {
                    return; // 继续轮询
                }
                
                // 多次失败后停止轮询
                clearInterval(pollInterval);
                message.warning({ 
                    content: `无法获取批次 "${batchName}" 的状态，请手动前往列表页查看`, 
                    key: `polling_${batchId}`,
                    duration: 5
                });
                
                setCalculating(false);
            }
        }, 5000); // 每5秒轮询一次
    };

    // 处理批次冲突
    const handleBatchConflict = (conflictData, formValues) => {
        const existingBatch = conflictData.existing_batch;
        
        Modal.confirm({
            title: '发现相同时间范围的批次',
            width: 600,
            content: (
                <div style={{ marginTop: 16 }}>
                    <Alert
                        message="已存在相同时间范围的批次"
                        description="创建新批次将删除现有批次及其所有相关数据，包括航班利润记录和异常记录。"
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    
                    <Descriptions title="现有批次信息" size="small" column={1} bordered>
                        <Descriptions.Item label="批次名称">{existingBatch.batch_name}</Descriptions.Item>
                        <Descriptions.Item label="批次类型">{existingBatch.type}</Descriptions.Item>
                        <Descriptions.Item label="时间范围">
                            {existingBatch.start_date} ~ {existingBatch.end_date}
                        </Descriptions.Item>
                        <Descriptions.Item label="批次状态">
                            <Badge 
                                status={existingBatch.status === 'COMPLETED' ? 'success' : 
                                       existingBatch.status === 'RUNNING' ? 'processing' : 'default'} 
                                text={existingBatch.status} 
                            />
                        </Descriptions.Item>
                        <Descriptions.Item label="总成本">¥{existingBatch.total_cost.toLocaleString()}</Descriptions.Item>
                        <Descriptions.Item label="航班数量">{existingBatch.total_flights} 个航班</Descriptions.Item>
                        <Descriptions.Item label="创建时间">{existingBatch.created_at}</Descriptions.Item>
                        {existingBatch.completed_at && (
                            <Descriptions.Item label="完成时间">{existingBatch.completed_at}</Descriptions.Item>
                        )}
                    </Descriptions>
                    
                    <div style={{ marginTop: 16, color: '#ff4d4f', fontSize: '14px' }}>
                        <strong>⚠️ 警告：</strong>选择"替换现有批次"将：
                        <ul style={{ marginTop: 8, paddingLeft: 16 }}>
                            <li>永久删除上述批次的所有数据</li>
                            <li>删除所有关联的航班利润记录</li>
                            <li>删除所有航线匹配异常记录</li>
                            <li>此操作不可撤销</li>
                        </ul>
                    </div>
                </div>
            ),
            okText: '替换现有批次',
            okButtonProps: { danger: true },
            cancelText: '取消',
            onOk: () => {
                // 用户确认替换，重新提交带replace_existing=true的请求
                message.loading({ content: '正在替换现有批次...', key: 'batch', duration: 0 });
                handleSubmit(formValues, true);
            },
            onCancel: () => {
                setCalculating(false);
            }
        });
    };

    return (
        <div style={{ padding: '16px', maxWidth: 1200, margin: '0 auto' }}>
            {/* 页面标题 */}
            <Card style={{ marginBottom: 16 }}>
                <Space>
                    <Button 
                        icon={<ArrowLeftOutlined />} 
                        onClick={() => navigate('/settlement/batch-calculations')}
                    >
                        返回列表
                    </Button>
                    <h2 style={{ margin: 0 }}>创建批次核算</h2>
                </Space>
            </Card>

            {/* 表单区域 */}
            <Card title="基本信息">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        type: 'OFFICIAL',
                        date_range: [dayjs().startOf('month'), dayjs().endOf('month')]
                    }}
                >
                    <Form.Item
                        label="批次名称"
                        name="batch_name"
                        rules={[
                            { required: true, message: '请输入批次名称' },
                            { min: 3, message: '批次名称至少3个字符' },
                            { max: 255, message: '批次名称不能超过255个字符' }
                        ]}
                    >
                        <Input 
                            placeholder="例如: 2024-10月度结算-最终版" 
                            disabled={calculating}
                        />
                    </Form.Item>

                    <Form.Item
                        label="批次类型"
                        name="type"
                        rules={[{ required: true, message: '请选择批次类型' }]}
                        tooltip="正式: 正式结算批次 | 草稿: 试算批次 | 调整: 调整之前批次 | 测试: 测试用"
                    >
                        <Select placeholder="请选择批次类型" disabled={calculating}>
                            <Option value="OFFICIAL">正式</Option>
                            <Option value="DRAFT">草稿</Option>
                            <Option value="ADJUSTMENT">调整</Option>
                            <Option value="TEST">测试</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="时间范围"
                        name="date_range"
                        rules={[{ required: true, message: '请选择时间范围' }]}
                        tooltip="基于航班实际起飞时间(ATD)进行筛选"
                    >
                        <RangePicker 
                            style={{ width: '100%' }}
                            format="YYYY-MM-DD"
                            disabled={calculating}
                        />
                    </Form.Item>

                    <Form.Item
                        label="批次说明"
                        name="description"
                    >
                        <TextArea 
                            rows={4} 
                            placeholder="可选: 输入批次说明信息" 
                            disabled={calculating}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button 
                                onClick={() => navigate('/settlement/batch-calculations')}
                                disabled={calculating}
                            >
                                取消
                            </Button>
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                loading={loading}
                                icon={<ThunderboltOutlined />}
                                disabled={calculating}
                            >
                                开始计算
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            {/* 计算进度 */}
            {calculating && (
                <Card title="核算进度" style={{ marginTop: 16 }}>
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                        <Alert
                            message="批次核算进行中"
                            description="系统正在执行航线匹配和成本聚合，这可能需要几分钟时间，请耐心等待..."
                            type="info"
                            showIcon
                        />
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <Spin size="large" tip="核算中..." />
                        </div>
                    </Space>
                </Card>
            )}
        </div>
    );
};

export default BatchCalculationCreatePage;

